import { yupResolver } from '@hookform/resolvers/yup';
import clsx from 'clsx';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import React, { useEffect, useState } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';

import {
  useUpdateApprovers,
  useUpdateInventoryApprovers,
} from '@/modules/settings/approvals/apiHooks';
import { useInventoryApprovals } from '@/modules/settings/approvals/components/Inventory/logic';
import EditableApproverRows from '@/modules/settings/approvals/components/shared/ApprovalDetails/ApproverUsersList/EditableApproverRows';
import ViewApproverRows from '@/modules/settings/approvals/components/shared/ApprovalDetails/ApproverUsersList/ViewApproverRows';
import ApprovalEditModal from '@/modules/settings/approvals/components/shared/ApprovalEditModal';
import { useApprovals } from '@/modules/settings/approvals/components/shared/logic';
import TableHeader from '@/modules/settings/approvals/components/shared/TableHeader';
import { IApprovals } from '@/modules/settings/approvals/types';
import { Button, Typography } from '@/shared/components/atoms';
import { useModal } from '@/shared/hooks/context/modalContext/useModal';

import Edit from '~/images/shared/edit.svg';

interface IProps {
  approvers: IApprovals.IApproverItem[];
  approvalPathId: number;
  entityId: number;
}
const ApproverUsersList = ({ approvers, approvalPathId, entityId }: IProps) => {
  // Hooks
  const { t } = useTranslation('common');
  const { query } = useRouter();
  const { openModal, closeModal } = useModal();

  // State
  const [isEdit, setIsEdit] = useState(false);

  // Form
  const { updateApproversSchema } = useApprovals();
  const formMethods = useForm<IApprovals.IUpdateApproverSchema>({
    resolver: yupResolver(updateApproversSchema),
    mode: 'all',
  });

  // API
  const { mutate: updateTechnicalApprovers } = useUpdateApprovers();
  const { mutate: updateInventoryApprovers } = useUpdateInventoryApprovers();
  const { getUpdateInventoryApprovalMutation } = useInventoryApprovals();

  // Handlers
  const handleAddEditApprover = () => {
    setIsEdit(true);
  };
  const onSuccess = () => {
    closeModal();
    setIsEdit(false);
    toast.success(t('approvers-updated-successfully'));
  };

  // Form submission
  const onSubmit: SubmitHandler<IApprovals.IUpdateApproverSchema> = (
    values
  ) => {
    if (query?.['tab'] === 'operations') {
      updateTechnicalApprovers(values, { onSuccess });
    } else {
      updateInventoryApprovers(getUpdateInventoryApprovalMutation(values), {
        onSuccess,
      });
    }
  };

  // Effects
  useEffect(() => {
    formMethods.reset({
      approvers: approvers.length
        ? approvers.map((item) => ({
            approverId: item.id,
            canEdit: item.canEdit,
          }))
        : [
            {
              canEdit: 1,
            },
          ],
      approvalPathId,
      entityId,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [approvers]);

  return (
    <FormProvider {...formMethods}>
      <form>
        <div className='border-y-xs border-neutralVariant-90   pt-30 '>
          {/* ******************************* table header ************************************ */}
          <TableHeader />
          {/* ************************** approver list edit/view ****************************** */}
          {isEdit ? (
            <EditableApproverRows />
          ) : (
            <ViewApproverRows approvers={approvers} />
          )}
        </div>

        {/* ************************************ actions ************************************** */}
        <div
          className={clsx(
            ' rounded-bl-2xl rounded-br-2xl  px-24 py-8',
            isEdit ? 'bg-white' : 'bg-background-neutral-100'
          )}
        >
          <div className='flex items-center justify-between'>
            <div className={clsx('inline-block')}>
              {!isEdit && (
                <Button
                  onClick={handleAddEditApprover}
                  variant='text'
                  size='full'
                >
                  <Typography
                    variant='subtitle1'
                    weight='semiBold'
                    className={clsx(
                      'flex items-center justify-center gap-8 p-16  text-primary-default',
                      !isEdit && 'rounded-xl border-xs border-outline-default '
                    )}
                  >
                    <>
                      <span className='text-primary-default'>
                        <Edit />
                      </span>
                      {t('add-or-edit-approver')}
                    </>
                  </Typography>
                </Button>
              )}
            </div>
            {isEdit && (
              <div className='flex items-center gap-32'>
                <div className='inline-block'>
                  <Button
                    onClick={() => setIsEdit(false)}
                    variant='text'
                    size='full'
                    className='text-error-400'
                  >
                    <Typography
                      className='text-error-400'
                      variant='subtitle1'
                      weight='semiBold'
                    >
                      {t('cancel')}
                    </Typography>
                  </Button>
                </div>
                <div className='inline-block'>
                  <Button
                    size='lg'
                    onClick={() =>
                      openModal(
                        <ApprovalEditModal
                          handleSubmit={formMethods.handleSubmit((values) =>
                            onSubmit(values)
                          )}
                        />
                      )
                    }
                  >
                    <Typography>{t('save-edits')}</Typography>
                  </Button>
                </div>
              </div>
            )}
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
export default ApproverUsersList;
